import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Activity, ChevronLeft, ChevronRight, Filter } from 'lucide-react';

const ACTION_COLORS = {
  'status_change': 'bg-blue-50 text-blue-700', 'bulk_status_change': 'bg-indigo-50 text-indigo-700',
  'bulk_email': 'bg-purple-50 text-purple-700', 'auto_portal_quote_email': 'bg-green-50 text-green-700',
  'bulk_portal_generate': 'bg-amber-50 text-amber-700',
};

export default function AuditLogPage() {
  const { api } = useAuth();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [entityFilter, setEntityFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/audit-log?page=${page}&limit=30&entity_type=${entityFilter}`);
      setLogs(data.items || []);
      setTotal(data.total || 0);
      setPages(data.pages || 1);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [api, page, entityFilter]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="audit-log-page" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-extrabold tracking-tight text-slate-900 font-[Manrope]">Audit Log</h1>
          <p className="text-sm text-slate-500 mt-1">{total} actions tracked</p>
        </div>
        <div className="flex items-center gap-2">
          <Filter size={14} className="text-slate-400" />
          <select value={entityFilter} onChange={e => { setEntityFilter(e.target.value); setPage(1); }} className="px-3 py-1.5 border border-[#E2E2DF] rounded-md text-sm">
            <option value="">All Entities</option>
            <option value="appraisal">Appraisals</option>
            <option value="client">Clients</option>
          </select>
        </div>
      </div>
      {loading ? <div className="text-center py-12 text-slate-400">Loading...</div> : (
        <div className="bg-white border border-[#E2E2DF] rounded-md overflow-hidden">
          <table className="w-full">
            <thead><tr className="bg-slate-50">
              {['Time', 'Action', 'Entity', 'User', 'Details'].map(h => <th key={h} className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">{h}</th>)}
            </tr></thead>
            <tbody>
              {logs.map(l => (
                <tr key={l._id} className="border-t border-[#E2E2DF] hover:bg-slate-50">
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{l.created_at ? new Date(l.created_at).toLocaleString() : '-'}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${ACTION_COLORS[l.action] || 'bg-slate-100 text-slate-600'}`}>{(l.action || '').replace(/_/g, ' ')}</span></td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-slate-600">{l.entity_type}</span>
                    {l.entity_id && <button onClick={() => { const paths = { appraisal: '/appraisals', client: '/clients' }; if (paths[l.entity_type]) navigate(`${paths[l.entity_type]}/${l.entity_id}`); }} className="text-xs text-[#D35400] ml-1 hover:underline">{l.entity_id?.slice(-6)}</button>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{l.user_name || l.user_id === 'system' ? 'System' : l.user_id?.slice(-6) || '-'}</td>
                  <td className="px-4 py-3 text-xs text-slate-500">
                    {l.details && typeof l.details === 'object' ? Object.entries(l.details).map(([k, v]) => <span key={k} className="mr-2">{k}: <strong>{String(v)}</strong></span>) : '-'}
                  </td>
                </tr>
              ))}
              {logs.length === 0 && <tr><td colSpan={5} className="px-4 py-12 text-center text-slate-400">No audit entries yet. Actions will appear as you use the system.</td></tr>}
            </tbody>
          </table>
          {pages > 1 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E2DF]">
              <span className="text-xs text-slate-500">Page {page} of {pages}</span>
              <div className="flex gap-2">
                <button disabled={page <= 1} onClick={() => setPage(p => p - 1)} className="p-1.5 rounded border disabled:opacity-30"><ChevronLeft size={16} /></button>
                <button disabled={page >= pages} onClick={() => setPage(p => p + 1)} className="p-1.5 rounded border disabled:opacity-30"><ChevronRight size={16} /></button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
